import {useRef, useState, useEffect, FC} from "react";

import styles from "./Chat.module.css";
import {Modal, Button} from "semantic-ui-react";
import {useSavedPrompts} from "../../hooks/useSavedPrompts";

import {RAGChatCompletion, RAGChatCompletionDelta} from "../../api";
import {
    AIChatProtocolClient,
    AIChatMessage,
    AIChatCompletionOperationOptions
} from "@microsoft/ai-chat-protocol";
import {Answer, AnswerError, AnswerLoading} from "../../components/Answer";

import {ExampleList} from "../../components/Example";
import {UserChatMessage} from "../../components/UserChatMessage";
import {ClearChatButton} from "../../components/ClearChatButton";
import {SavedPromptsButton} from "../../components/SavedPromptsButton/SavedPromptsButton";
import {SavedPromptsModal} from "../../components/SavedPromptsModal/SavedPromptsModal";

import {AppState} from "@/state/store";
import {getCurrentSelectedBusinessUnitId} from "@/util/stateUtil";
import {connect, ConnectedProps} from "react-redux";
import {QuestionInput} from "../../components/QuestionInput/QuestionInput";
import {actionCreators} from "@/features/authentication/state/actions";
import {AuthenticationAuthorizationResult} from "@/features/authentication/state/reducer";
import {sortingDirections} from "@/util/sortUtil";
import {HttpErrorType} from "@/util/errorUtil";

interface IChatAnswer {
    id: string;
    question: string;
    response: RAGChatCompletion;
    fileContent?: string;
    fileName?: string;
}

interface IRequestMetadata {
    id: string;
    question: string;
    fileContent?: string;
    fileName?: string;
}

enum ConversationTerminationStatus {
    None,
    Terminated,
    AccessRevoked
}

const Chat: FC<IReduxProps> = (props) => {
    const coachAiIcon = new URL(
        "@/coach-ai/src/assets/CoachAI_Icon.svg",
        import.meta.url
    ).href;
    const coachAiHeader = new URL(
        "@/coach-ai/src/assets/CoachAI_Header.svg",
        import.meta.url
    ).href;

    const nextRequestIdRef = useRef<number>(0);
    const lastRequestRef = useRef<IRequestMetadata | null>(null);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    // Abort controller for cancelling requests
    const abortControllerRef = useRef<AbortController | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<string>("");
    const [isSessionTimeout, setIsSessionTimeout] = useState<boolean>(false);

    // used to restore the QuestionInput after a request is cancelled
    const [cancelledQuestion, setCancelledQuestion] = useState<string>("");

    const [answers, setAnswers] = useState<IChatAnswer[]>([]);
    const [streamedAnswer, setStreamedAnswer] = useState<IChatAnswer | null>(null);
    const [conversationTerminated, setConversationTerminated] =
        useState<boolean>(false);
    const [isAccessRevoked, setIsAccessRevoked] = useState<boolean>(false);

    // Use the custom hook for saved prompts
    const {
        isSavedPromptsOpen,
        savedPrompts,
        openSavedPrompts,
        closeSavedPrompts,
        handleSavePrompt,
        handleAddPrompt,
        handleEditPrompt,
        handleDeletePrompt
    } = useSavedPrompts();

    useEffect(() => {
        // Authenticate the user when the component mounts
        authenticateChat();
    }, []);

    useEffect(() => {
        if (props.isAuthenticated !== AuthenticationAuthorizationResult.Success) {
            setIsSessionTimeout(true);
        } else {
            setIsSessionTimeout(false);
        }
    }, [props.isAuthenticated]);

    useEffect(() => {
        setSelectedBusinessUnitId(props.businessUnitIdFromCoachGroupTree);
    }, [props.businessUnitIdFromCoachGroupTree]);

    const authenticateChat = async () => {
        props.authenticateViaIdentity();
    };

    const handleAsyncRequest = async (
        requestId: string,
        question: string,
        result: AsyncIterable<RAGChatCompletionDelta>,
        fileContent?: string,
        fileName?: string,
        signal?: AbortSignal
    ) => {
        let answer = "";
        const chatCompletion: RAGChatCompletion = {
            context: {
                data_points: {},
                data_points_metadata: {
                    columns_metadata: [],
                    sort_column: "",
                    sort_direction: sortingDirections.Descending
                },
                highchart_data: null,
                followup_questions: null,
                datadog_trace_id: "",
            },
            message: {content: "", role: "assistant"}
        };
        const updateState = (newContent: string) => {
            return new Promise((resolve) => {
                setTimeout(() => {
                    answer += newContent;
                    // We need to create a new object to trigger a re-render
                    const latestCompletion: RAGChatCompletion = {
                        ...chatCompletion,
                        message: {content: answer, role: chatCompletion.message.role}
                    };
                    setStreamedAnswer({
                        id: requestId,
                        question,
                        response: latestCompletion,
                        fileContent,
                        fileName
                    });
                    resolve(null);
                }, 33);
            });
        };
        let terminationStatus = ConversationTerminationStatus.None;

        try {
            setIsStreaming(true);
            for await (const response of result) {
                // Stop processing if aborted
                if (signal?.aborted) break;

                if (response.context?.malicious_detected) {
                    terminationStatus = ConversationTerminationStatus.Terminated;
                }
                if (response.context?.diagnosis_report) {
                    console.dir(response.context.diagnosis_report);
                }
                if (response.is_access_revoked) {
                    terminationStatus = ConversationTerminationStatus.AccessRevoked;
                }
                if (response.context) {
                    const nonNullContext = Object.keys(response.context).reduce(
                        (acc, key) => {
                            const value = (
                                response.context as Record<string, unknown>
                            )[key];
                            if (value !== null && value !== undefined) {
                                acc[key] = value;
                            }
                            return acc;
                        },
                        {} as Record<string, unknown>
                    );
                    chatCompletion.context = {
                        ...chatCompletion.context,
                        ...nonNullContext
                    };
                }
                if (response.sessionState) {
                    chatCompletion.sessionState = response.sessionState as object;
                }
                if (response.delta && response.delta.role) {
                    chatCompletion.message.role = response.delta.role;
                }
                if (response.delta && response.delta.content) {
                    setIsLoading(false);
                    await updateState(response.delta.content);
                }
            }
        } finally {
            setIsStreaming(false);
            setStreamedAnswer(null);
        }
        chatCompletion.message.content = answer;
        return {chatCompletion, terminationStatus};
    };
    const makeApiRequest = async (
        question: string,
        fileContent?: string,
        fileName?: string
    ) => {
        // Prevent starting another request while one is running
        if (isLoading || isStreaming) return;

        // Must do this because api/CoachAssistant/chat/stream not being called through apiUtil, so validateResponse() is not called to appropriately handle 401
        await authenticateChat();

        nextRequestIdRef.current += 1;
        const requestId = nextRequestIdRef.current.toString();

        lastRequestRef.current = {
            id: requestId,
            question,
            fileContent,
            fileName
        };

        error && setError(undefined);
        setIsLoading(true);
        setCancelledQuestion("");

        // Create an AbortController for this request
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const messages: AIChatMessage[] = answers.flatMap((answer) => [
                {content: answer.question, role: "user"},
                {content: answer.response.message.content, role: "assistant"}
            ]);

            // If file content is provided, modify the question to include it
            const userMessage = fileContent
                ? `${question}\n\nData from uploaded file:\n${fileContent}`
                : question;

            const allMessages: AIChatMessage[] = [
                ...messages,
                {content: userMessage, role: "user"}
            ];

            const options: AIChatCompletionOperationOptions = {
                context: {
                    currentSelectedBusinessUnitId: selectedBusinessUnitId
                },
                sessionState: {
                    ...((answers.length
                        ? (answers[answers.length - 1].response
                              .sessionState as object)
                        : null) ?? {})
                },
                abortSignal: controller.signal
            };

            const chatEndpointUrl = "/api/CoachAssistant/chat";

            const chatClient: AIChatProtocolClient = new AIChatProtocolClient(
                chatEndpointUrl
            );

            const result = (await chatClient.getStreamedCompletion(
                allMessages,
                options
            )) as AsyncIterable<RAGChatCompletionDelta>;

            const {
                chatCompletion: parsedResponse,
                terminationStatus
            } = await handleAsyncRequest(
                requestId,
                question,
                result,
                fileContent,
                fileName,
                controller.signal
            );

            if (terminationStatus === ConversationTerminationStatus.AccessRevoked) {
                setIsAccessRevoked(true);
                setConversationTerminated(true);
            } else if (terminationStatus === ConversationTerminationStatus.Terminated) {
                setConversationTerminated(true);
            }

            // only append the final answer if the request was not cancelled
            if (!controller.signal.aborted) {
                setAnswers((prev) => [
                    ...prev,
                    {
                        id: requestId,
                        question,
                        response: parsedResponse,
                        fileContent,
                        fileName
                    }
                ]);
            }
        } catch (e: any) {
            // If abort() was called, don't show a cancellation error
            if (
                e?.name !== HttpErrorType.Abort &&
                !abortControllerRef.current?.signal.aborted
            ) {
                setError(e);
            }
        } finally {
            setIsLoading(false);
            // clear abort controller reference
            abortControllerRef.current = null;
        }
    };

    const cancelRequest = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();

            // restore the input to the question that was sent
            setCancelledQuestion(lastRequestRef.current?.question ?? "");

            // Update UI
            setIsLoading(false);
            setIsStreaming(false);
        }
    };

    const clearChat = () => {
        lastRequestRef.current = null;
        error && setError(undefined);
        setCancelledQuestion("");
        setAnswers([]);
        setStreamedAnswer(null);
        setIsLoading(false);
        setIsStreaming(false);
        conversationTerminated && setConversationTerminated(false);
        isAccessRevoked && setIsAccessRevoked(false);
    };

    const handleSelectSavedPrompt = (promptText: string) => {
        makeApiRequest(promptText);
    };

    useEffect(
        () => chatMessageStreamEnd.current?.scrollIntoView({behavior: "smooth"}),
        [isLoading]
    );
    useEffect(
        () => chatMessageStreamEnd.current?.scrollIntoView({behavior: "auto"}),
        [streamedAnswer]
    );

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    if (isSessionTimeout) {
        // If the session has timed out, we don't render the chat UI
        // This is just an extra layer of protection...
        // Once 401 is dispatched from api/authenticate it shows SessionTimeout
        return;
    }

    return (
        <div className={styles.container} style={{marginBottom: "25px"}}>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastRequestRef.current?.question ? (
                        <div className={styles.chatEmptyState}>
                            <img
                                src={coachAiIcon}
                                alt="CoachAI Icon"
                                style={{height: "250px", borderRadius: "15px"}}
                            />
                            <img
                                src={coachAiHeader}
                                alt="CoachAI"
                                className={styles.coachAIHeader}
                            />
                            <h2 className={styles.chatSubtitle}>
                                Your data analytics AI assistant
                            </h2>
                            <br></br>
                            <ExampleList onExampleClicked={onExampleClicked} />
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {answers.map((answer) => (
                                <div key={answer.id}>
                                    <UserChatMessage
                                        message={answer.question}
                                        hasFileAttached={
                                            answer.fileName !== undefined
                                        }
                                        attachedFileName={answer.fileName}
                                        onSavePrompt={() =>
                                            handleSavePrompt(answer.question)
                                        }
                                        isSaved={savedPrompts.some(
                                            (prompt) =>
                                                prompt.promptText === answer.question
                                        )}
                                    />
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            isStreaming={false}
                                            key={answer.id}
                                            answer={answer.response}
                                            onFollowupQuestionClicked={(q) =>
                                                makeApiRequest(q)
                                            }
                                            onRetryClicked={() =>
                                                makeApiRequest(
                                                    answer.question,
                                                    answer.fileContent,
                                                    answer.fileName
                                                )
                                            }
                                            isRequestActive={
                                                isLoading ||
                                                isStreaming ||
                                                conversationTerminated
                                            }
                                            promptText={answer.question}
                                            userId={props.userInfo?.clientUserID}
                                        />
                                    </div>
                                </div>
                            ))}
                            {isStreaming && streamedAnswer && (
                                <div key={`streaming-${streamedAnswer.id}`}>
                                    <UserChatMessage
                                        message={streamedAnswer.question}
                                        hasFileAttached={
                                            streamedAnswer.fileName !== undefined
                                        }
                                        attachedFileName={streamedAnswer.fileName}
                                        onSavePrompt={() =>
                                            handleSavePrompt(streamedAnswer.question)
                                        }
                                        isSaved={savedPrompts.some(
                                            (prompt) =>
                                                prompt.promptText ===
                                                streamedAnswer.question
                                        )}
                                    />
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            isStreaming={true}
                                            key="streaming-answer-current"
                                            answer={streamedAnswer.response}
                                            onFollowupQuestionClicked={(q) =>
                                                makeApiRequest(q)
                                            }
                                            promptText={streamedAnswer.question}
                                            userId={props.userInfo?.clientUserID}
                                        />
                                    </div>
                                </div>
                            )}
                            {isLoading && (
                                <>
                                    <UserChatMessage
                                        message={
                                            lastRequestRef.current?.question || ""
                                        }
                                        hasFileAttached={
                                            lastRequestRef.current?.fileName !==
                                            undefined
                                        }
                                        attachedFileName={
                                            lastRequestRef.current?.fileName
                                        }
                                    />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <UserChatMessage
                                        message={
                                            lastRequestRef.current?.question || ""
                                        }
                                        hasFileAttached={
                                            lastRequestRef.current?.fileName !==
                                            undefined
                                        }
                                        attachedFileName={
                                            lastRequestRef.current?.fileName
                                        }
                                    />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError
                                            error={error.toString()}
                                            onRetry={() => {
                                                const retryQuestion =
                                                    lastRequestRef.current?.question;
                                                if (retryQuestion) {
                                                    makeApiRequest(
                                                        retryQuestion,
                                                        lastRequestRef.current
                                                            ?.fileContent,
                                                        lastRequestRef.current
                                                            ?.fileName
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                </>
                            ) : null}
                            <Modal
                                open={isAccessRevoked}
                                size="small"
                                closeOnDimmerClick={false}
                                closeOnEscape={false}
                            >
                                <Modal.Header>Access Disabled</Modal.Header>
                                <Modal.Content>
                                    <p>
                                        Your access to Coach AI has been disabled due
                                        to repeated violations of our content policy.
                                        Contact support to have your access restored.
                                    </p>
                                </Modal.Content>
                                <Modal.Actions>
                                    <Button
                                        primary
                                        onClick={() => window.location.reload()}
                                    >
                                        Refresh Page
                                    </Button>
                                </Modal.Actions>
                            </Modal>
                            {conversationTerminated && !isAccessRevoked && (
                                <div className={styles.conversationTerminatedBanner}>
                                    <strong>Conversation Terminated</strong>
                                    <p>
                                        This conversation has been terminated due to
                                        a policy violation. Please clear the chat and
                                        start a new conversation.
                                    </p>
                                </div>
                            )}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}
                    <SavedPromptsModal
                        isOpen={isSavedPromptsOpen}
                        onClose={closeSavedPrompts}
                        onSelectPrompt={handleSelectSavedPrompt}
                        onAddPrompt={handleAddPrompt}
                        onEditPrompt={handleEditPrompt}
                        onDeletePrompt={handleDeletePrompt}
                        savedPrompts={savedPrompts}
                    />
                    <div className={styles.chatInput}>
                        <div className={styles.commandsContainer}>
                            <SavedPromptsButton
                                className={styles.commandButton}
                                onClick={openSavedPrompts}
                                disabled={conversationTerminated || isAccessRevoked}
                            />
                            <ClearChatButton
                                className={styles.commandButton}
                                onClick={clearChat}
                                disabled={
                                    !lastRequestRef.current?.question || isLoading
                                }
                            />
                        </div>
                        <QuestionInput
                            clearOnSend
                            placeholder="Ask me how to improve your operations"
                            onSend={(question, fileContent, fileName) =>
                                makeApiRequest(question, fileContent, fileName)
                            }
                            handleBusinessUnitChange={setSelectedBusinessUnitId}
                            selectedBusinessUnitId={selectedBusinessUnitId}
                            clientTreeHierarchy={props.clientTreeHierarchy}
                            isRequestActive={isLoading || isStreaming}
                            disabled={conversationTerminated || isAccessRevoked}
                            initQuestion={cancelledQuestion}
                            onCancel={cancelRequest}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Create the Redux connector
const mapStateToProps = (state: AppState) => {
    return {
        clientTreeHierarchy: state.application.clientTreeHierarchy,
        businessUnitIdFromCoachGroupTree: getCurrentSelectedBusinessUnitId(state),
        isAuthenticated: state.auth.isAuthenticated,
        userInfo: state.application.userInfo
    };
};

// Create and export the connected component with proper typing
const connector = connect(mapStateToProps, actionCreators);

type IReduxProps = ConnectedProps<typeof connector>;
const ConnectedChat = connector(Chat);
export default ConnectedChat;
